import { useCallback, useMemo, useRef, useState } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/layout';
import { Badge, Button, Card, EmptyState, EntityLink, getStatusBadgeVariant } from '@/components/common';
import { PolicyNumber } from '@/components/common/PolicyNumber';
import { getErrorMessage } from '@/services/api';
import { formatDate, formatNumber, titleCase } from '@/utils';
import {
  ACCEPTED_EXTENSIONS,
  downloadMissingReport,
  downloadVerifyTemplate,
  filterResults,
  formatRows,
  matchedOnLabel,
  validateUploadFile,
  verdictText,
  verifyPolicyNumbers,
} from './bulkVerify';
import type { BulkVerifyResponse, ResultTab } from './bulkVerify';

const TABS: { key: ResultTab; label: string }[] = [
  { key: 'missing', label: 'Not found' },
  { key: 'found', label: 'Found' },
  { key: 'duplicates', label: 'Duplicates in file' },
  { key: 'all', label: 'All' },
];

export function BulkPolicyVerifyPage() {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<BulkVerifyResponse | null>(null);
  // The file that produced `result`. The report download re-posts THIS file,
  // so it must not change when the user picks a new one without re-verifying.
  const [verifiedFile, setVerifiedFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [tab, setTab] = useState<ResultTab>('missing');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const rows = useMemo(() => filterResults(result, tab), [result, tab]);

  const counts = useMemo(
    () => ({
      missing: result?.summary.missing_count ?? 0,
      found: result?.summary.found_count ?? 0,
      duplicates: result?.summary.duplicate_numbers ?? 0,
      all: result?.summary.unique_numbers ?? 0,
    }),
    [result]
  );

  const chooseFile = useCallback((selected: File | null) => {
    if (!selected) return;
    const error = validateUploadFile(selected);
    if (error) {
      toast.error(error);
      return;
    }
    setFile(selected);
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      setIsDragging(false);
      chooseFile(event.dataTransfer.files?.[0] ?? null);
    },
    [chooseFile]
  );

  const clearFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleVerify = async () => {
    const error = validateUploadFile(file);
    if (error || !file) {
      toast.error(error ?? 'Choose a file to verify.');
      return;
    }
    setBusy(true);
    try {
      const data = await verifyPolicyNumbers(file);
      setResult(data);
      setVerifiedFile(file);
      setTab(data.summary.missing_count > 0 ? 'missing' : 'found');
      if (data.summary.rows_truncated) {
        toast('Only the first 20,000 rows were checked.', { icon: '⚠️' });
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDownloadReport = async (fileFormat: 'csv' | 'xlsx') => {
    if (!verifiedFile) return;
    setDownloading(true);
    try {
      await downloadMissingReport(verifiedFile, fileFormat);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDownloading(false);
    }
  };

  const handleTemplate = async (fileFormat: 'csv' | 'xlsx') => {
    try {
      await downloadVerifyTemplate(fileFormat);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div>
      <PageHeader
        title="Verify Policies"
        subtitle="Upload a list of policy numbers to find out which ones are not in your book"
        breadcrumbs={[{ label: 'Policies', href: '/policies' }, { label: 'Verify' }]}
      />

      {/* Upload */}
      <Card className="mb-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Upload your list</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              CSV or Excel, one policy number per row. Add a <code>policy_number</code> header, or just
              paste a single column of numbers. We match your own policy numbers and the insurer&apos;s.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<DocumentTextIcon className="h-4 w-4" />}
              onClick={() => handleTemplate('csv')}
            >
              CSV template
            </Button>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<DocumentTextIcon className="h-4 w-4" />}
              onClick={() => handleTemplate('xlsx')}
            >
              Excel template
            </Button>
          </div>
        </div>

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setIsDragging(false);
          }}
          onDrop={handleDrop}
          className={`mt-4 rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
            isDragging
              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
              : 'border-gray-300 dark:border-gray-600'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_EXTENSIONS.join(',')}
            className="hidden"
            onChange={(e) => chooseFile(e.target.files?.[0] ?? null)}
          />
          {file ? (
            <div className="flex items-center justify-center gap-3">
              <DocumentTextIcon className="h-6 w-6 text-primary-600" />
              <span className="text-sm font-medium text-gray-900 dark:text-white">{file.name}</span>
              <span className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</span>
              <button
                type="button"
                onClick={clearFile}
                aria-label="Remove file"
                className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <>
              <ArrowUpTrayIcon className="mx-auto h-8 w-8 text-gray-400" />
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                Drag a file here, or{' '}
                <button
                  type="button"
                  className="font-medium text-primary-600 hover:underline"
                  onClick={() => fileInputRef.current?.click()}
                >
                  browse
                </button>
              </p>
              <p className="mt-1 text-xs text-gray-400">.csv, .xlsx or .xls — up to 10MB</p>
            </>
          )}
        </div>

        <div className="mt-4 flex justify-end">
          <Button onClick={handleVerify} isLoading={busy} disabled={!file || busy}>
            Verify {counts.all > 0 ? 'again' : 'policy numbers'}
          </Button>
        </div>
      </Card>

      {result && (
        <>
          {/* Verdict */}
          <div
            className={`mb-6 flex items-start gap-3 rounded-xl border p-4 ${
              result.summary.missing_count === 0
                ? 'border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-900/20'
                : 'border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-900/20'
            }`}
          >
            {result.summary.missing_count === 0 ? (
              <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
            ) : (
              <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            )}
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {verdictText(result.summary)}
              </p>
              {/* The check is scoped to this broker's own policies, so "not
                  found" never means "does not exist anywhere". Saying so up
                  front stops a broker reading the report as proof a policy is
                  fake when it may simply sit with someone else. */}
              <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                Checked against your own policies only. A number recorded under another broker will
                show as not found.
              </p>
            </div>
            {result.summary.missing_count > 0 && (
              <div className="flex shrink-0 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<ArrowDownTrayIcon className="h-4 w-4" />}
                  onClick={() => handleDownloadReport('csv')}
                  isLoading={downloading}
                >
                  CSV
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<ArrowDownTrayIcon className="h-4 w-4" />}
                  onClick={() => handleDownloadReport('xlsx')}
                  disabled={downloading}
                >
                  Excel
                </Button>
              </div>
            )}
          </div>

          {/* Summary tiles */}
          <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <SummaryTile label="Numbers in file" value={result.summary.total_values} />
            <SummaryTile label="Unique numbers" value={result.summary.unique_numbers} />
            <SummaryTile label="Found" value={result.summary.found_count} tone="success" />
            <SummaryTile label="Not found" value={result.summary.missing_count} tone="warning" />
          </div>

          {result.summary.duplicate_values > 0 && (
            <div className="mb-6 flex items-start gap-2 rounded-lg bg-blue-50 p-3 text-sm text-blue-800 dark:bg-blue-900/20 dark:text-blue-200">
              <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {result.summary.duplicate_numbers} number
                {result.summary.duplicate_numbers === 1 ? ' appears' : 's appear'} more than once in
                your file ({result.summary.duplicate_values} extra row
                {result.summary.duplicate_values === 1 ? '' : 's'}). Each is checked and reported once.
              </span>
            </div>
          )}

          {/* Results */}
          <Card padding="none">
            <div className="flex flex-wrap border-b border-gray-200 dark:border-gray-700">
              {TABS.map((entry) => (
                <button
                  key={entry.key}
                  onClick={() => setTab(entry.key)}
                  className={`px-5 py-3 text-sm font-medium transition-colors ${
                    tab === entry.key
                      ? 'border-b-2 border-primary-600 text-primary-600'
                      : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  {entry.label}
                  <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                    {counts[entry.key]}
                  </span>
                </button>
              ))}
            </div>

            {rows.length === 0 ? (
              <div className="p-6">
                <EmptyState
                  title="Nothing to show"
                  description={
                    tab === 'missing'
                      ? 'Every policy number in the file was found in your policies.'
                      : 'No rows in this view.'
                  }
                />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-900/40">
                    <tr>
                      <Th>Policy number</Th>
                      <Th>Result</Th>
                      <Th>Matched policy</Th>
                      <Th>Holder</Th>
                      <Th>Expiry</Th>
                      <Th>File rows</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {rows.map((item) => (
                      <tr key={item.number} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                          {item.number}
                          {item.occurrences > 1 && (
                            <span className="ml-2 rounded bg-blue-50 px-1.5 py-0.5 text-xs text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                              ×{item.occurrences}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {item.exists ? (
                            <div className="flex flex-col gap-1">
                              <Badge variant="success" size="sm">
                                Found
                              </Badge>
                              {item.match && (
                                <span className="text-xs text-gray-500">
                                  {matchedOnLabel(item.match.matched_on)}
                                </span>
                              )}
                            </div>
                          ) : (
                            <Badge variant="warning" size="sm">
                              Not found
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {item.match ? (
                            <div className="flex flex-col gap-1">
                              <PolicyNumber
                                source={item.match}
                                render={(text) => (
                                  <EntityLink kind="policy" id={item.match!.policy_id} name={text} />
                                )}
                              />
                              {item.match.status && (
                                <Badge variant={getStatusBadgeVariant(item.match.status)} size="sm">
                                  {titleCase(item.match.status)}
                                </Badge>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                          {item.match?.holder_name || <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                          {item.match?.expiry_date ? (
                            formatDate(item.match.expiry_date)
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-500">{formatRows(item.rows)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {((tab === 'missing' && result.missing_truncated) ||
              (tab !== 'missing' && result.results_truncated)) && (
              <div className="border-t border-gray-100 px-4 py-3 text-xs text-gray-500 dark:border-gray-700">
                Showing the first 2,000 rows. Download the report for the complete list.
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
      {children}
    </th>
  );
}

function SummaryTile({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: number;
  tone?: 'default' | 'success' | 'warning';
}) {
  const toneStyles = {
    default: 'text-gray-900 dark:text-white',
    success: 'text-green-600 dark:text-green-400',
    warning: 'text-amber-600 dark:text-amber-400',
  };
  return (
    <Card>
      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</p>
      <p className={`mt-2 text-3xl font-bold ${toneStyles[tone]}`}>{formatNumber(value)}</p>
    </Card>
  );
}
